import { useEffect, useState } from "react";
import RuleItemBuilder from "./RuleItemBuilder";
import { fromJS } from "immutable";
import { CheckCircleIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import RuleAction from "../../actions/RuleAction";
import TemplateAction from "../../actions/TemplateAction";
import { useDispatch, useSelector } from "react-redux";
import { SubmitRuleForm } from "../../api/NetTypes";
import { RuleItemTypes, TemplateContexts } from "../../constants/action-types";
import RuleLogList from "./RuleLogList";
import { Link } from "react-router-dom";
import BucketAction from "../../actions/BucketAction";
import RuleValidator from "./RuleValidator";
import { useTranslation } from "react-i18next";
import ChartLoading from "../../insight/components/ChartLoading";

type RuleFormProps = {
  onSaveRule?: () => void;
  projectId: string;
  ruleId: string;
  hideRunsPanel?: boolean;
};

export const isReadOnlyAction = (ruleItem) => {
  switch (ruleItem.value) {
    case "convert_hubspot_contact_to_sale":
    case "hubspot_incoming_webhook":
    case "jsm_request":
    case "convert_jsm_request_to_sale":
      return true;
    default:
      return false;
  }
};

export const RuleForm = ({
  onSaveRule = () => {},
  projectId,
  ruleId,
  hideRunsPanel,
}: RuleFormProps) => {
  const dispatch = useDispatch();

  const rule = useSelector((state: any) => state.ruleReducer.get("rule"));

  const ruleAction = RuleAction as any;

  const [ruleForm, setRuleForm] = useState<SubmitRuleForm>({
    events: [],
    actions: [],
    projectId,
    id: rule.get("id") || "",
    enabled: true,
  });

  const emptyRuleItem = {
    data: {},
    value: "",
  };

  const newRule = {
    projectId,
    enabled: true,
    events: [emptyRuleItem],
    actions: [emptyRuleItem],
  };

  const isNewRule = ruleId == "new";

  const { t } = useTranslation();

  useEffect(() => {
    setRuleForm({
      events: rule.get("events").toJS(),
      actions: rule.get("actions").toJS(),
      projectId,
      enabled: rule.get("enabled"),
      id: rule.get("id") || "",
    });
  }, [rule.get("id")]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isNewRule) {
      setRuleForm(newRule);
    } else {
      dispatch(RuleAction.getRule(ruleId));
    }
    dispatch(BucketAction.getBuckets(projectId, "SALE"));
    dispatch(TemplateAction.getAttributes([TemplateContexts.SALE, TemplateContexts.RECIPIENT]));
  }, [ruleId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isValidRule = () => {
    const inValidActions = ruleForm["actions"].filter((item) => RuleValidator(item)() == false);
    const inValidEvents = ruleForm["events"].filter((item) => RuleValidator(item)() == false);
    if (inValidActions.length > 0 || inValidEvents.length > 0) {
      return false;
    }
    return true;
  };

  const handleSaveRule = async () => {
    if (isValidRule()) {
      await dispatch(ruleAction.saveRule(ruleForm));
      onSaveRule();
    }
  };

  const handleFieldChange = (fieldId: string, index: number, fieldValue: any) => {
    const isEventChange = fieldId === "events" && index === 0;
    const eventChanged =
      isEventChange &&
      ruleForm.events[0] &&
      (ruleForm.events[0].value !== fieldValue?.value ||
        ruleForm.events[0].data?.inputType !== fieldValue?.data?.inputType);

    setRuleForm({
      ...ruleForm,
      [fieldId]: ruleForm[fieldId].map((item: any, i: number) => (i === index ? fieldValue : item)),
      // Clear actions when the trigger event changes so the user re-adds actions for the new type
      ...(eventChanged ? { actions: [emptyRuleItem] } : {}),
    });
  };

  const handleRemoveRuleItem = (type, ruleIndex) => {
    let removedList = fromJS(ruleForm[type]);
    if (ruleIndex == 0) {
      removedList = removedList.set(0, emptyRuleItem);
    } else {
      removedList = removedList.delete(ruleIndex);
    }

    setRuleForm({
      ...ruleForm,
      [type]: removedList.toJS(),
    });
  };
  const handleAddRuleItem = (type, ruleIndex) => {
    const items = fromJS(ruleForm[type]);
    const newList = items.insert(ruleIndex + 1, emptyRuleItem);
    setRuleForm({
      ...ruleForm,
      [type]: newList.toJS(),
    });
  };

  const isEdit = rule.get("id");

  const getActionInputType = (ruleItem, index) => {
    if (index == 0) {
      return ruleForm.events[0].data.inputType || "";
    }
    return ruleForm.actions[index - 1].data.outputType || "";
  };

  const renderRuleItemAction = (type, index) => {
    const ruleItem = ruleForm[type][index];
    const isValid = RuleValidator(ruleItem)();
    const validCSS = isValid ? "text-green-600" : "text-amber-600";
    const showAdd = isValid && type == "actions";
    return (
      <div className={"absolute right-6 flex space-x-2 cursor-pointer"}>
        <CheckCircleIcon className={`${validCSS} w-8`} />
        {index > 0 && (
          <TrashIcon
            className={"w-6 text-gray-400"}
            onClick={() => {
              handleRemoveRuleItem(type, index);
            }}
          />
        )}
        {showAdd && (
          <PlusIcon
            className={"w-8 text-blue-600 shadow-sm"}
            onClick={() => {
              handleAddRuleItem(type, index);
            }}
          />
        )}
      </div>
    );
  };

  const isLoadingRule = !isNewRule && rule.get("id") !== ruleId;

  const createButtonCss = isValidRule() ? "bg-blue-500 text-white" : "bg-blue-500 text-gray-400";

  const submitLabel = () => {
    return rule.get("id") ? t("Update Automation") : t("Create Automation");
  };

  return (
    <>
      <div className="flex w-full gap-0 min-h-0">
        <div
          className={`flex-1 min-w-0 p-4 bg-white relative ${isEdit ? "border-r border-gray-200" : ""}`}
        >
          <div className={"pb-2 text-lg font-bold"}>{t("Event")}</div>
          {ruleForm.events.map((ruleItem, index) => (
            <div
              key={`action_${ruleItem.key}_${index}`}
              className={"w-full shadow-sm hover:bg-gray-50 h-fit p-4 flex"}
            >
              <RuleItemBuilder
                rule={rule}
                value={ruleItem}
                readonly={isReadOnlyAction(ruleItem)}
                isNewRule={isNewRule}
                itemType={RuleItemTypes.EVENT}
                placeholder={t("When this happens")}
                onChange={(value: any) => {
                  handleFieldChange("events", index, value);
                }}
              />
              {renderRuleItemAction("events", index)}
            </div>
          ))}

          <div className={"pb-2 pt-2 text-lg font-bold"}>{t("Actions")}</div>
          {isLoadingRule ? (
            <div className={"w-full shadow-sm h-fit p-4"}>
              <ChartLoading height={300} label={t("Loading...")} />
            </div>
          ) : (
            ruleForm.actions.map((ruleItem, index) => {
              const inputType = getActionInputType(ruleItem, index);
              return (
                <div
                  key={`action_${ruleItem.key}_${index}_${inputType}`}
                  className={"w-full shadow-sm h-fit p-4 flex"}
                >
                  <RuleItemBuilder
                    rule={rule}
                    value={ruleItem}
                    isNewRule={isNewRule}
                    itemType={RuleItemTypes.ACTION}
                    readonly={isReadOnlyAction(ruleItem)}
                    inputType={inputType}
                    placeholder={t("Then do this")}
                    onChange={(value: any) => {
                      handleFieldChange("actions", index, value);
                    }}
                  />
                  {renderRuleItemAction("actions", index)}
                </div>
              );
            })
          )}
          <div className={"pt-4 flex"}>
            <button className={`rounded-sm p-2 w-fit ${createButtonCss}`} onClick={handleSaveRule}>
              {submitLabel()}
            </button>
            <div className={"w-2"}></div>
            <Link
              className={
                "pointer-events-auto rounded-md px-4 py-2 text-center font-medium shadow-xs ring-1 ring-slate-700/10 hover:bg-slate-50 w-32"
              }
              to={`/workspaces/${projectId}/automations`}
            >
              {t("Cancel")}
            </Link>
          </div>
        </div>
        {!hideRunsPanel && ruleId !== "new" && (
          <aside className="w-[720px] min-w-[520px] shrink-0 flex flex-col bg-gray-50 border-l border-gray-200 overflow-hidden h-[100vh] min-h-0">
            <RuleLogList showLog={{ ruleId }} projectId={projectId} sidebar />
          </aside>
        )}
      </div>
    </>
  );
};
